import _ from 'lodash';

import DynamicLeaves from '../../NextTree/plugins/DynamicLeaves';
import Expansion from '../../NextTree/plugins/Expansion';
import LazyLoading from '../../NextTree/plugins/LazyLoading';
import NodeClassNames from '../../NextTree/plugins/NodeClassNames';
import NodeCustomText from '../../NextTree/plugins/NodeCustomText';
import NodeIcon from '../../NextTree/plugins/NodeIcon';
import NodePopover from '../../NextTree/plugins/NodePopover';
import NodesMeta from '../../NextTree/plugins/NodesMeta';
import NodeSorting from '../../NextTree/plugins/NodeSorting';
import SubtreeFilter from '../../NextTree/plugins/SubtreeFilter';
import TotalLeafCounter from '../../NextTree/plugins/TotalLeafCounter';
import { isRootNode } from '../constants';

const getId = model =>
  isRootNode(model) || _.isUndefined(model.id) ? null : model.id;

export default function makeTreePlugins(
  {
    nodeIsVisible,
    nodeCanLoadChildren,
    selectionMode,
    nodeIsSelectable,
    nodeSortingOptions,
    getNodeParentId,
    nodeExpansionOptions,
    nodeTotalLeafCounterOptions,
    lazyLoadingOptions,
    hasDynamicLeaves,
    addNodeBuildMeta: buildMeta,
    resolveNodeClassName: nodeClassName,
    resolveNodeIconName: nodeIconName,
    resolveNodeCustomText: nodeCustomText,
    resolveNodePopoverText: nodePopoverText,
  },
  { client, query, variables, t, permission } = {},
) {
  if (query && lazyLoadingOptions) {
    throw Error('Only single lazy load options can be passed');
  }

  const extraPlugins = [];

  if (selectionMode) {
    const plugin = selectionMode.makeTreePlugin(nodeIsSelectable);
    if (plugin) {
      extraPlugins.push(plugin);
    }
  }

  // to store number of children even if there is no buildMeta provided
  extraPlugins.push(new NodesMeta({ buildMeta }));

  if (nodeIsVisible) {
    extraPlugins.push(new SubtreeFilter({ nodeIsVisible }));
  }

  if (nodeTotalLeafCounterOptions && nodeTotalLeafCounterOptions.isEnabled) {
    extraPlugins.push(new TotalLeafCounter(nodeTotalLeafCounterOptions));
  }

  if (nodeExpansionOptions && !nodeExpansionOptions.isDisabled) {
    extraPlugins.push(new Expansion(nodeExpansionOptions));
  } else if (nodeExpansionOptions && nodeExpansionOptions.isDisabled) {
    // nothing to do
  } else {
    extraPlugins.push(new Expansion());
  }

  if (lazyLoadingOptions) {
    extraPlugins.push(new LazyLoading(lazyLoadingOptions));
  }

  if (query) {
    extraPlugins.push(
      new LazyLoading({
        reloadSeedData: false,

        nodeCanLoadChildren: node =>
          node && (nodeCanLoadChildren === true || nodeCanLoadChildren(node)),
        createObservableQuery: model =>
          client.watchQuery({
            fetchResults: true,
            query,
            variables: { ...variables, parentId: getId(model), permission },
          }),

        t,
      }),
    );
  }

  if (nodeSortingOptions) {
    extraPlugins.push(
      new NodeSorting({
        iteratees: nodeSortingOptions.iteratees,
        orders: nodeSortingOptions.orders,
        getNodeParentId,
      }),
    );
  }

  if (hasDynamicLeaves) {
    extraPlugins.push(new DynamicLeaves());
  }

  if (nodeIconName) {
    extraPlugins.push(new NodeIcon({ nodeIconName }));
  }

  if (nodeCustomText) {
    extraPlugins.push(new NodeCustomText({ nodeCustomText }));
  }

  if (nodePopoverText) {
    extraPlugins.push(new NodePopover({ nodePopoverText }));
  }

  return [...extraPlugins, new NodeClassNames({ nodeClassName })];
}
